import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Typography,
  IconButton
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CircleIcon from '@mui/icons-material/Circle';
import { ContextMenu } from '../../foundation/shared/components/context-menu';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import { getWorkspaceFullscreen$ } from '@client/widgets/layouts/workspace-fullscreen.service';
import { DataFeedInput } from '@server/data-feed/schemas/input.schema';
import { colorTokens } from '@client/foundation/design-system/tokens';
import { useWidget } from '@client/widgets/widget.hook';

type SideMenuProps = {
  open: boolean;
  feedInputConfigs: DataFeedInput[];
  onToggleMenu: () => void;
  onLoadChart: (config: DataFeedInput) => void;
  onClose: (config: DataFeedInput) => void;
  onDuplicate: (config: DataFeedInput) => void;
  onClearSavedTrades: (config: DataFeedInput) => void;
};

export const SideMenu: React.FC<SideMenuProps> = ({
  open,
  feedInputConfigs,
  onToggleMenu,
  onLoadChart,
  onClose,
  onDuplicate,
  onClearSavedTrades
}) => {
  const { openWidget } = useWidget();
  const [menuAnchor, setMenuAnchor] = useState<{
    mouseX: number;
    mouseY: number;
    config: DataFeedInput | null;
  } | null>(null);
  const workspaceFullscreen = useObservable(() => getWorkspaceFullscreen$());

  // Handle opening the context menu
  const handleContextMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, config: DataFeedInput) => {
      event.preventDefault();
      setMenuAnchor({
        mouseX: event.clientX,
        mouseY: event.clientY,
        config
      });
    },
    []
  );

  // Handle closing the context menu
  const handleContextMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  // Define context menu options
  const contextMenuOptions = useMemo(
    () => [
      {
        label: 'Load Chart',
        onClick: () => {
          if (menuAnchor?.config) {
            onLoadChart(menuAnchor.config); // Call the parent handler to load the chart
          }
          handleContextMenuClose();
        }
      },
      {
        label: 'Close',
        onClick: () => {
          if (menuAnchor?.config) {
            const selectedConfig = menuAnchor.config;
            openWidget({
              type: 'ConfirmationDialog',
              title: 'Close feed connection?',
              description: `This will close ${selectedConfig.marketConfig.symbol} on ${selectedConfig.marketConfig.exchangeId} and clear its saved trades.`,
              confirmLabel: 'Close',
              intent: 'danger',
              onConfirm: () => onClose(selectedConfig)
            });
          }
          handleContextMenuClose();
        }
      },
      {
        label: 'Duplicate',
        onClick: () => {
          if (menuAnchor?.config) {
            onDuplicate(menuAnchor.config);
          }
          handleContextMenuClose();
        }
      },
      {
        label: 'Clear saved trades',
        onClick: () => {
          if (menuAnchor?.config) {
            const selectedConfig = menuAnchor.config;
            openWidget({
              type: 'ConfirmationDialog',
              title: 'Clear saved trades?',
              description: `This will remove saved trades for feed ${selectedConfig.feedId}. Current running feed state will stay unchanged.`,
              confirmLabel: 'Clear',
              intent: 'danger',
              onConfirm: () => onClearSavedTrades(selectedConfig)
            });
          }
          handleContextMenuClose();
        }
      }
    ],
    [
      menuAnchor?.config,
      handleContextMenuClose,
      onClose,
      onDuplicate,
      onLoadChart,
      onClearSavedTrades,
      openWidget
    ]
  );

  // Memoize the list of feed items to avoid unnecessary re-renders
  const feedList = useMemo(
    () =>
      feedInputConfigs.map((feed, index) => (
        <FeedListItem
          key={index}
          feed={feed}
          onContextMenu={(event) => handleContextMenuOpen(event, feed)}
        />
      )),
    [feedInputConfigs, handleContextMenuOpen]
  );

  return (
    <Box
      width={workspaceFullscreen ? 0 : open ? 250 : 42}
      bgcolor={colorTokens.slate950Alpha95}
      p={workspaceFullscreen ? 0 : 0.4}
      overflow={workspaceFullscreen ? 'hidden' : 'auto'}
      flexShrink={0}
      display="flex"
      flexDirection="column"
    >
      {/* Menu Toggle Icon */}
      {!workspaceFullscreen && (
        <Box display="flex" justifyContent={open ? 'flex-end' : 'center'}>
          <IconButton onClick={onToggleMenu} size="medium">
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {open && !workspaceFullscreen && <Divider sx={{ my: 1 }} />}

      {open && (
        <>
          <List dense>{feedList}</List>
          {feedInputConfigs.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No live connections available.
            </Typography>
          )}
        </>
      )}

      {/* Context Menu */}
      <ContextMenu
        anchor={
          menuAnchor
            ? { mouseX: menuAnchor.mouseX, mouseY: menuAnchor.mouseY }
            : null
        }
        onClose={handleContextMenuClose}
        options={contextMenuOptions}
      />
    </Box>
  );
};

// Separate component for rendering a single feed list item
type FeedListItemProps = {
  feed: DataFeedInput;
  onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
};

const FeedListItem: React.FC<FeedListItemProps> = ({ feed, onContextMenu }) => {
  const { marketConfig, tradingScheduleConfig } = feed;

  return (
    <ListItem
      divider
      // onContextMenu={onContextMenu}
      onClick={onContextMenu}
      sx={{ alignItems: 'center' }}
    >
      {/* Indicator Light */}
      <CircleIcon
        fontSize="small"
        sx={{
          color: tradingScheduleConfig?.trade ? 'green' : 'red',
          mr: 1 // Add margin to the right
        }}
      />

      <ListItemText
        primary={`${marketConfig.symbol} - ${marketConfig.exchangeId}`}
        secondary={
          marketConfig.testnet ? (
            <Chip label="Testnet" size="small" color="warning" />
          ) : null
        }
      />
    </ListItem>
  );
};
